import React from "react";
import { Helmet } from "react-helmet";
import { ShareSection } from "../../components/template";

const Blog8 = () => {
  const hashtags = [
    "#consciousliving",
    "#mindsetgrowth",
    "#intentionalhabits",
    "#selfawareness",
    "#ParamLife",
  ];

  return (
    <>
      {/* 🧠 SEO Meta Tags */}
      <Helmet>
        <title>If You Don’t Control Your Mind — Someone Else Will</title>
        <meta
          name="title"
          content="If You Don’t Control Your Mind — Someone Else Will"
        />
        <meta
          name="description"
          content="Every day, the world reminds you what to want. Learn how to stop external influences from shaping your desires and start living with conscious intention."
        />
        <meta
          name="keywords"
          content="conscious living, intentional living, digital detox, live with purpose, self awareness, conscious choices, mindfulness, mindset growth, Param Life, Indian wellness"
        />
        <meta name="author" content="Param Life" />
        <meta
          property="og:title"
          content="If You Don’t Control Your Mind — Someone Else Will"
        />
        <meta
          property="og:description"
          content="Every day, the world reminds you what to want. Learn how to stop external influences from shaping your desires and start living with conscious intention."
        />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Param Life" />
        <meta property="og:locale" content="en_IN" />
      </Helmet>

      {/* 🌿 Blog Body */}
      <article className="max-w-4xl mx-auto px-6 py-4 leading-relaxed">
                <h1 className="text-secondary text-3xl font-semibold mb-8 mt-8 text-left">
          If You Don’t Control Your Mind — Someone Else Will
        </h1>

        {/* 🖼️ Header Image */}
        <img
          src="/assets/knowyourself/blogs/blog8.png"
          alt="If You Don’t Control Your Mind — Someone Else Will"
          className="max-w-xl rounded-lg shadow-md mx-auto mb-8"
        />

        {/* 🪷 Title */}

        <p className="italic text-gray-600 mb-6">
          Every day, the world reminds you what to want. It’s time to take your
          attention back.
        </p>

        {/* 🌱 Section 1 */}
        <section className="space-y-4 mb-8">
          <p>
            Let me ask you something that probably annoys you too — how many
            times in a day do you get a sales call, see an ad, or hear a
            notification reminding you to buy, subscribe, or upgrade something?
          </p>

          <p>
            Chances are, you can’t even count them anymore. Everywhere you look
            — someone’s selling, nudging, or influencing you. Not always
            aggressively, but persistently.
          </p>

          <p>
            Because the truth is simple: what gets repeated, gets remembered. And
            what gets remembered, starts to shape what we want.
          </p>

          <p>
            That’s how our desires get programmed — not always by our conscious
            will, but by constant repetition from the world around us.
          </p>
        </section>

        {/* 🧭 Section 2 */}
        <section className="space-y-4 mb-8">
          <h2 className="text-primary text-2xl font-semibold text-left">
            The Subtle Hijack of Your Inner Voice
          </h2>

          <p>
            Every day, you’re being told what to want — from hundreds of brands
            through thousands of messages.
          </p>

          <ul className="list-disc list-inside space-y-1">
            <li>Buy this.</li>
            <li>Wear that.</li>
            <li>Eat this.</li>
            <li>Subscribe now.</li>
          </ul>

          <p>
            The result? We start losing touch with our own desires — the ones
            that arise from within. Our goals, values, and dreams begin to fade
            under the noise of algorithms and advertisements.
          </p>

          <p>We stop asking ourselves:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>What do I truly want from myself?</li>
            <li>What do I value most in life?</li>
            <li>What kind of person do I want to become?</li>
          </ul>

          <p>
            The external voices become louder than our own. And slowly, we start
            living a life designed by others.
          </p>
        </section>

        {/* 🔁 Section 3 */}
        <section className="space-y-4 mb-8">
          <h2 className="text-primary text-2xl font-semibold text-left">
            Repetition Works — Use It for Yourself
          </h2>

          <p>
            If repetition can shape the world’s influence on us… why not use the
            same tool for ourselves?
          </p>

          <p>
            Start creating your{" "}
            <span className="text-[#578d48]">inner frequency</span> — a simple
            daily system of gentle self-reminders that tune you back to what
            matters.
          </p>

          <p>Here’s how you can begin:</p>

          <ol className="list-decimal list-inside space-y-2">
            <li>
              <strong className="text-[#578d48]">Morning Intentions:</strong>{" "}
              Before touching your phone, take 3 slow breaths. Visualize your
              core values and one thing that truly matters today.
            </li>
            <li>
              <strong className="text-[#578d48]">Evening Reflection:</strong>{" "}
              Before sleeping, recall how aligned you felt today with your
              purpose and what you’d like to improve tomorrow.
            </li>
            <li>
              <strong className="text-[#578d48]">Personal Mantra:</strong> Write
              one line that defines your life’s direction — read it daily till it
              becomes your inner sound.
            </li>
          </ol>

          <p>
            Because the truth is — if you don’t consciously program your mind,
            someone else will.
          </p>
        </section>

        {/* 🌿 Section 4 */}
        <section className="space-y-4 mb-8">
          <h2 className="text-primary text-2xl font-semibold text-left">
            Your Inner Reminder
          </h2>

          <p>
            Every ad is an external reminder of someone else’s dream. Let your
            habits become reminders of{" "}
            <em className="text-[#578d48]">your</em> dream — your values, your
            purpose, your version of happiness.
          </p>

          <p>
            You deserve to live a life that feels{" "}
            <strong>authentic</strong> — not algorithmic.
          </p>
        </section>

        {/* 🏷️ Hashtags */}
        <div className="flex flex-wrap gap-2 mt-10">
          {hashtags.map((tag, idx) => (
            <span
              key={idx}
              className="bg-red-700 bg-opacity-10 text-red-700 hover:bg-red-700 hover:text-white rounded-full px-3 py-1 text-sm transition"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* 📤 Share Section */}
        <ShareSection />
      </article>
    </>
  );
};

export default Blog8;
